import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  DocumentArrowUpIcon, 
  DocumentArrowDownIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  UserIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL;

export default function PublicFileShare() {
  const { token } = useParams();
  const [fileShare, setFileShare] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedBy, setUploadedBy] = useState('');
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchFileShare();
  }, [token]);

  const fetchFileShare = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/file-share/${token}/`);
      const data = await response.json();
      
      if (data.success) {
        setFileShare(data.data);
        setFiles(data.data.files || []);
      } else {
        setError(data.message || 'File share not found');
      }
    } catch (error) {
      console.error('Error fetching file share:', error);
      setError('Failed to load file share');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      setError('File size must be less than 100MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'ppt', 'pptx', 'xls', 'xlsx'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      setError(`File type .${fileExtension} is not allowed`);
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      // Step 1: Get presigned URL for S3 upload
      const initResponse = await fetch(`${API_URL}/api/file-share/${token}/s3-upload/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          uploaded_by: uploadedBy || 'Anonymous'
        })
      });

      const initData = await initResponse.json();
      if (!initData.success) {
        throw new Error(initData.message || 'Failed to get presigned URL');
      }

      // Step 2: Upload file to S3 using presigned URL with progress tracking
      const uploadResponse = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve(xhr);
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', initData.data.presigned_url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Step 3: Confirm upload and create database record
      const confirmResponse = await fetch(`${API_URL}/api/file-share/${token}/s3-confirm/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          s3_key: initData.data.s3_key,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          uploaded_by: uploadedBy || 'Anonymous'
        })
      });

      const confirmData = await confirmResponse.json();
      if (confirmData.success) {
        setFiles(prev => [confirmData.data, ...prev]);
        setUploadProgress(100);
        setTimeout(() => {
          setUploading(false);
          setUploadProgress(0);
        }, 1000);
      } else {
        throw new Error(confirmData.message || 'Failed to confirm upload');
      }

    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.message || 'Upload failed');
      setUploading(false);
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const response = await fetch(`${API_URL}/api/file-share/${token}/download/${fileId}/`);
      const data = await response.json();
      
      if (data.success) {
        // New S3 format: response contains download_url
        if (data.data.download_url) {
          // Fetch the file content from S3
          const fileResponse = await fetch(data.data.download_url);
          if (fileResponse.ok) {
            const blob = await fileResponse.blob();
            
            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = data.data.file_name || fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up the object URL
            window.URL.revokeObjectURL(url);
          } else {
            setError('Failed to download file from S3');
          }
        } else {
          setError('Download URL not found');
        }
      } else {
        setError(data.message || 'Download failed');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Download failed');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-sm text-gray-500">Loading file share...</p>
        </div>
      </div>
    );
  }

  if (error && !fileShare) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {fileShare?.title || 'File Sharing'}
              </h1>
              {fileShare?.description && (
                <p className="mt-2 text-sm text-gray-600">{fileShare.description}</p>
              )}
              {fileShare?.demo_info && (
                <div className="mt-3 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Demo:</strong> {fileShare.demo_info.student_name} with {fileShare.demo_info.tutor_name} - {fileShare.demo_info.subject}
                  </p>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="flex items-center text-sm text-gray-500">
                <CalendarDaysIcon className="h-4 w-4 mr-1" />
                <span>{files.length} files</span>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Area */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Upload Files</h2>
          
          {/* Uploader Name Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Name (Optional)
            </label>
            <input
              type="text"
              value={uploadedBy}
              onChange={(e) => setUploadedBy(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Drag & Drop Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-indigo-500 bg-indigo-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  Drop files here or click to upload
                </span>
                <span className="mt-1 block text-sm text-gray-500">
                  PDF, DOC, DOCX, TXT, JPG, PNG, GIF, PPT, PPTX, XLS, XLSX (max 100MB)
                </span>
              </label>
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                className="sr-only"
                onChange={(e) => handleFileUpload(e.target.files[0])}
                disabled={uploading}
              />
            </div>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Uploading...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Files List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Shared Files</h2>
          </div>
          
          {files.length === 0 ? (
            <div className="text-center py-12">
              <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No files yet</h3>
              <p className="mt-1 text-sm text-gray-500">Upload the first file to get started.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {files.map((file) => (
                <div key={file.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DocumentIcon className="h-8 w-8 text-gray-400" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-900">{file.file_name}</p>
                        <div className="flex items-center text-sm text-gray-500">
                          <span>{formatFileSize(file.file_size)}</span>
                          <span className="mx-2">•</span>
                          <span>{formatDate(file.uploaded_at)}</span>
                          <span className="mx-2">•</span>
                          <div className="flex items-center">
                            <UserIcon className="h-4 w-4 mr-1" />
                            <span>{file.uploaded_by}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(file.id, file.file_name)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
